/**
 * Identità della build della console, e come accorgersi che è vecchia.
 *
 * Il problema che risolve è concreto: un `index.html` rimasto in cache fa
 * caricare il bundle precedente, e da dentro la console non si distingue da un
 * difetto. Si finisce a cercare il guasto in un codice che è già corretto e
 * semplicemente non è quello in esecuzione.
 *
 * Il valore viene inciso a compile time da `vite.config.ts` in due copie — nel
 * bundle e in un meta tag del documento — e il confronto fra le due è la
 * risposta.
 */

import { localeCorrente, t } from './i18n/lingua'

/** Timbro inciso nel bundle in esecuzione. */
export const BUILD: { sha: string; builtAt: string } =
  typeof __CENSO_BUILD__ !== 'undefined'
    ? __CENSO_BUILD__
    : // Ambiente che non passa dal define di Vite (un test, per esempio):
      // dichiarare di non sapere è meglio che far sembrare noto un valore finto.
      { sha: 'sconosciuta', builtAt: '' }

/** Etichetta breve per l'interfaccia. */
export function buildLabel(): string {
  const sha = BUILD.sha === 'sconosciuta' ? t('sconosciuta') : BUILD.sha
  if (!BUILD.builtAt) return sha
  const when = new Date(BUILD.builtAt)
  if (Number.isNaN(when.getTime())) return sha
  return `${sha} · ${when.toLocaleString(localeCorrente(), {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })}`
}

/**
 * Chiede al server quale build sta servendo adesso.
 *
 * ``null`` quando non si riesce a stabilirlo — rete assente, documento senza
 * timbro perché servito da un deploy più vecchio di questa funzione. In quel
 * caso non si dice nulla: un falso allarme sulla versione costerebbe più
 * fiducia di quanta ne guadagni.
 *
 * ``cache: 'no-store'`` non è un dettaglio: interrogare la cache per sapere se
 * la cache è vecchia darebbe sempre la risposta sbagliata.
 */
export async function servedBuild(): Promise<string | null> {
  try {
    const resp = await fetch(`${document.baseURI}?build-check`, { cache: 'no-store' })
    if (!resp.ok) return null
    const html = await resp.text()
    const match = html.match(/<meta[^>]+name=["']censo-build["'][^>]+content=["']([^"']+)["']/i)
    return match ? match[1] : null
  } catch {
    return null
  }
}

/** Vero quando il server serve una build diversa da quella in esecuzione. */
export async function isStale(): Promise<boolean> {
  const served = await servedBuild()
  // Un timbro sconosciuto da una delle due parti non è una prova di nulla: si
  // resta in silenzio invece di chiedere un ricaricamento che potrebbe non
  // cambiare niente.
  if (!served || served === 'sconosciuta' || BUILD.sha === 'sconosciuta') return false
  return served !== BUILD.sha
}
